<template>
    <div class="a-form-inputs">
        <form @submit.prevent class="m-form" name="pastLossOfEarnings">
            <h2 slot="header">
                Pre-accident Earnings
            </h2>

            <div class="flex-grid a-form-inputs">
                <div class="flex-grid__item">
                    <div class="u-margin-bottom-20 u-bold">
                        Is there any past loss of earnings?
                    </div>
                    <control for="past_loss_of_earnings" :table-control="true">
                        <select id="past_loss_of_earnings"
                                v-model="pastLossOfEarnings"
                                v-field-control
                                required>
                            <option :value="null" disabled hidden>Please select...</option>
                            <option :value="true">Yes</option>
                            <option :value="false">No</option>
                        </select>
                    </control>
                </div>
                <div class="flex-grid__item  flex-grid--space-left" v-if="pastLossOfEarnings">
                    <div class="u-margin-bottom-20 u-bold">
                        Calculate past losses or enter your own
                    </div>
                    <control for="past_loss_of_earnings" :table-control="true">
                        <select id="own_past_losses"
                                name="own_past_losses"
                                v-model="ownPastLosses"
                                v-field-control
                                required>
                            <option :value="null" disabled hidden>Please select...</option>
                            <option :value="false">Calculate for me</option>
                            <option :value="true">Enter your own figure</option>
                        </select>
                    </control>
                </div>
            </div>
            <div v-if="pastLossOfEarnings && !ownPastLosses">
                <div class="flex-grid a-form-inputs">
                    <div class="flex-grid__item">
                        <div class="u-d-flex">
                            <div class="u-margin-bottom-20 u-bold">
                                Employment Income
                            </div>
                            <icon-tooltip
                                    tooltip-name="pre-accident-income"
                                    tooltip-text="This is the claimant's income at the date of the accident."
                                    side="right"
                                    text-width="200"
                                    icon-type="info"
                                    icon-colour="m-icon--dark">
                            </icon-tooltip>
                        </div>
                        <control for="employment_income" :table-control="true" before-after="pound">
                            <input autocomplete="off"
                                   name="employment_income"
                                   id="employment_income"
                                   type="number"
                                   v-on:focus="employmentIncome = employmentIncome || ''"
                                   v-on:blur="employmentIncome = employmentIncome || 0"
                                   v-two-dp
                                   required
                                   v-model.number="employmentIncome">
                            <control-error :show="$error('employment_income')">
                                This is the claimant's income at the date of the accident is required
                            </control-error>
                        </control>
                    </div>
                    <div class="flex-grid__item flex-grid--space-left">
                        <div class="u-margin-bottom-20 u-bold">
                            Net/Gross
                        </div>
                        <control for="employment_net_gross" :table-control="true">
                            <select v-model="employmentNetGross" :disabled="disableNetGross">
                                <option value="net">Net</option>
                                <option value="gross">Gross</option>
                            </select>
                        </control>
                    </div>
                </div>

                <div class="flex-grid a-form-inputs">
                    <div class="flex-grid__item">
                        <div class="u-margin-bottom-20 u-bold">
                            Total Net Income
                        </div>
                        <control for="total_net_income" :table-control="true" before-after="pound">
                            <input autocomplete="off" type="number" v-two-dp disabled v-model="totalNetIncome">
                        </control>
                    </div>
                    <div class="flex-grid__item flex-grid--space-left">
                        <div class="u-margin-bottom-20 u-bold">
                            Employment Type
                        </div>
                        <control for="employment_type" :table-control="true">
                            <select v-model="employmentType">
                                <option value="employed">Employed</option>
                                <option value="self_employed">Self-employed</option>
                                <option value="part_employed">Part-employed</option>
                            </select>
                        </control>
                    </div>
                </div>

                <div class="flex-grid__item flex-grid--space-left">
                    <div class="u-d-flex">
                        <div class="u-margin-bottom-20 u-bold">
                            Update Total Net Income
                        </div>
                        <div class="p-pi-income__ashe-average-tooltip">
                            <modal-in-the-wild modal-name="pre-accident-income_ashe-average">
                                <div class="p-pi-income__ashe-average-tooltip">
                                    <icon-tooltip
                                            tooltip-text="Click to view information"
                                            side="right"
                                            text-width="140"
                                            icon-type="info"
                                            icon-colour="m-icon--dark">
                                    </icon-tooltip>
                                </div>
                            </modal-in-the-wild>
                        </div>
                    </div>
                    <button class="a-button a-button--small ashe p-pi-income__ashe-average-button"
                            @click="selectSalary(null)">
                        Use ASHE average
                    </button>
                    <button class="a-button a-button--small u-no-margin refine" @click="openDialog">
                        Refine by occupation
                    </button>
                </div>
            </div>
            <div v-if="pastLossOfEarnings && ownPastLosses">
                <div class="flex-grid a-form-inputs">
                    <div class="flex-grid__item">
                        <control for="employment_income" :table-control="true" before-after="pound">
                            <input autocomplete="off"
                                   name="past_loss_net"
                                   id="past_loss_net"
                                   type="number"
                                   v-two-dp
                                   required
                                   v-model.number="pastLossOfEarningsNetTotal">
                            <control-error :show="$error('pastLossOfEarningsNetTotal')">
                                You must enter a value for past losses.
                            </control-error>
                        </control>
                    </div>
                </div>
            </div>
        </form>

        <modal name="pre-accident-income_ashe-average" no-default-button="true">
            <h2>ASHE Average</h2>
            We have provided average gross (median) earnings by selected gender &amp; occupational categories according
            to the Average Survey of Hours and Earnings (ASHE). The data are collected and published by the Office of
            National Statics (ONS) annually and can be found here.
        </modal>

        <modal :name="'ons_salary_'+this.uniqueId" no-default-button="true">
            <div class="p-day-one__refine-occupation">
                <h2>Refine by occupation</h2>

                <div v-if="selectedNode">
                    <p>
                        Your current selection - <strong>{{ getLastNode().description }}</strong><br/>
                        <span v-if="getLastNode().code.length !== 3">Select a salary, expand down further or use the average salary for this whole group</span>
                        <span v-else>Select a salary or use the average salary for this whole group</span>
                    </p>

                    <button @click="unselectNode(selectedNode)" class="a-button a-button--small">Back</button>
                    <button @click="selectSalary(selectedNode)" class="a-button a-button--small">Use average
                        {{displaySalary(selectedNode)}}
                    </button>
                </div>
                <div v-else>
                    <p>
                        Select a salary or expand the group
                    </p>
                </div>
                <div>
                    <div>
                        <table class=" a-table__small a-table a-table--full-width">
                            <thead>
                            <tr>
                                <th>Code</th>
                                <th>Description</th>
                                <th>Salary</th>
                                <th width="30"></th>
                                <th width="30"></th>
                            </tr>
                            </thead>
                            <tbody v-if="loading">
                            <td colspan="5" style="text-align: center;">
                                <div class="a-loader--table"></div>
                            </td>
                            </tbody>
                            <tbody v-else>
                            <tr v-for="node in nodes" class="selected--row">
                                <td>{{node.code}}</td>
                                <td>{{node.description}}</td>
                                <td>{{displaySalary(node)}}</td>
                                <td>
                                    <button :disabled="node.code.length === 4" class="a-button a-button--small"
                                            @click.stop="selectNode(node)">
                                        Expand
                                    </button>
                                </td>
                                <td>
                                    <button v-if="node.salary!=='x'" class="a-button a-button--small"
                                            @click.stop="selectSalary(node, type)">
                                        Select
                                    </button>
                                </td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </modal>
    </div>
</template>

<script>
    import {mapActions, mapGetters} from 'vuex';
    import Control from 'components/form/Control';
    import ControlError from 'components/form/control/Error';
    import FormValidator from 'components/form/validator/mixin';
    import TwoDecimalPlaces from 'directives/TwoDecimalPlaces';
    import IconTooltip from 'components/IconTooltip';
    import Modal from 'components/modal/Modal';
    import ModalContent from 'components/modal/ModalContent';
    import ModalInTheWild from 'components/modal/ModalInTheWild';
    import Api from 'services/Api';
    import CurrencyFormat from 'mixins/currencyFormat';

    export default {
        name: 'pre-accident-earnings',
        data() {
            return {
                loading: false,
                nodes: [],
                selectedNode: null,
                selectedNodes: [],
                disableNetGross: false
            }
        },
        mounted() {
            this.updateValid();
        },
        mixins: [
            FormValidator,
            CurrencyFormat
        ],
        components: {
            Control,
            ControlError,
            IconTooltip,
            Modal,
            ModalContent,
            ModalInTheWild,
        },
        directives: {
            'two-dp': TwoDecimalPlaces
        },
        computed: {
            ...mapActions('personal/claimant', [
                'resetPostInjuryPeriod',
                'addIncomePeriod',
            ]),
            ...mapGetters('personal/claimant', [
                'claimant',
                'getIncomeValue',
                'validAbsence',
            ]),
            ...mapGetters('modal', ['modals']),

            pastLossOfEarnings: {
                get() {
                    return this.getIncomeValue('pastLossOfEarnings');
                },
                set(value) {
                    this.updateIncomeValue({key: 'pastLossOfEarnings', value})
                        .then(this.updateValid);
                }
            },
            otherPayments: {
                get() {
                    return this.getIncomeValue('otherPayments');
                },
                set(value) {
                    this.updateIncomeValue({key: 'otherPayments', value});
                }
            },
            pastPeriods() {
                return this.getIncomeValue('pastPeriods')
            },
            isModalOpen() {
                let ons_salary = 'ons_salary_' + this.uniqueId;
                return this.modals && this.modals[ons_salary];
            },
            uniqueId() {
                return this._uid;
            },
            employmentIncome: {
                get() {
                    return this.getIncomeValue('preAccident').employmentIncome;
                },
                set(value) {
                    this.disableNetGross = false;
                    this.updatePreAccidentEarningsValue({key: 'employmentIncome', value})
                        .then(this.updatePreAccidentEarnings);
                    this.updateValid();
                }
            },
            employmentNetGross: {
                get() {
                    return this.getIncomeValue('preAccident').employmentNetGross;
                },
                set(value) {
                    this.updatePreAccidentEarningsValue({key: 'employmentNetGross', value})
                        .then(this.updatePreAccidentEarnings);
                    this.updateValid();
                }
            },
            employmentType: {
                get() {
                    return this.getIncomeValue('preAccident').employmentType;
                },
                set(value) {
                    this.updatePreAccidentEarningsValue({key: 'employmentType', value})
                        .then(this.updatePreAccidentEarnings);
                    this.updateValid();
                }
            },
            ownPastLosses: {
                get() {
                    return this.getIncomeValue('ownPastLosses')
                },
                set(value) {
                    return this.updateIncomeValue({key: 'ownPastLosses', value})
                        .then(this.updateValid);
                }
            },
            pastLossOfEarningsNetTotal: {
                get() {
                    return this.getIncomeValue("pastLossOfEarningsNetTotal");
                },
                set(value) {
                    return this.updateIncomeValue({key: 'pastLossOfEarningsNetTotal', value})
                        .then(this.updateValid);
                }
            },
            ownFutureLosses: {
                get() {
                    return this.getIncomeValue('ownFutureLosses')
                },
                set(value) {
                    return this.updateIncomeValue({key: 'ownFutureLosses', value});
                }
            },
            totalNetIncome() {
                return this.getIncomeValue('preAccident').totalNetIncome;
            },
            ownMethodValid() {
                return this.pastLossOfEarnings === true && this.ownPastLosses && this.pastLossOfEarningsNetTotal > 0;
            }
        },
        methods: {
            ...mapActions('modal', ['toggle']),
            ...mapActions('personal/claimant', ['updateIncomeValue', 'updatePreAccidentEarnings', 'updatePreAccidentEarningsValue']),
            openDialog() {
                if (!this.isModalOpen) {
                    this.nodes = [];
                    this.selectedNode = null;
                    this.selectedNodes = [];
                    return this.toggle('ons_salary_' + this.uniqueId).then(this.updateNodes);
                }
            },
            closeModal() {
                if (this.isModalOpen) {
                    return this.toggle('ons_salary_' + this.uniqueId);
                }
            },
            updateNodes(code = null) {
                this.loading = true;
                Api.calculators.ons.salary(this.gender, code)
                    .then(nodes => {
                        this.nodes = nodes;
                        this.loading = false;
                    });
            },
            selectNode(node) {
                if (node.code.length === 4) {
                    return;
                }
                this.selectedNode = node;
                this.selectedNodes.push(node);
                return this.updateNodes(node.code);
            },
            unselectNode(node) {
                let index = this.selectedNodes.indexOf(node);
                if (index === 0) {
                    this.selectedNodes = [];
                    this.selectedNode = null;
                    return this.updateNodes();
                }
                this.selectedNodes.splice(index);
                let lastIndex = this.selectedNodes.length - 1;
                this.selectedNode = this.selectedNodes[lastIndex];
                this.updateNodes(this.selectedNode.code);
            },
            selectSalary(node) {
                if (node === null) {
                    return Api.calculators.ons.averageSalary(this.claimant.gender)
                        .then(result => {
                            this.employmentIncome = result.value;
                            this.updatePreAccidentEarningsValue({key: 'employmentNetGross', value: 'gross'});
                            this.disableNetGross = true;
                            this.updatePreAccidentEarnings();
                        }).then(this.closeModal);
                }
                this.employmentIncome = node.salary;
                this.updatePreAccidentEarnings();
                this.closeModal();
            },
            displaySalary(node) {
                if (node.salary === "x") {
                    return "No Information";
                }
                return this.currencyFormat(node.salary, '£', 0);
            },
            isValid() {
                if (this.ownMethodValid) {
                    return  true;
                }
                if (this.pastLossOfEarnings === false) {
                    return true
                }
                if (this.pastLossOfEarnings === true) {
                    if (this.ownPastLosses) {
                        return this.pastLossOfEarningsNetTotal > 0;
                    }
                    return this.employmentIncome > 0 && (this.totalNetIncome !== 0 || this.totalNetIncome !== '')
                }
                return false;
            },
            updateValid() {
                this.$emit('update', {
                    preAccidentEarningsValid: this.isValid(),
                    preAccidentEarnings: (this.pastLossOfEarnings || this.ownMethodValid)
                })
            },
            getLastNode() {
                return this.selectedNodes.slice(-1)[0];
            },
        }
    }
</script>


// WEBPACK FOOTER //
// PreAccidentEarnings.vue?16930aea